import {
  AlertTriangle,
  BarChart3,
  Briefcase,
  Building2,
  Bus,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  Clock,
  FileInput,
  FilePlus2,
  FileText,
  FolderOpen,
  GraduationCap,
  Handshake,
  Inbox,
  Landmark,
  LayoutDashboard,
  LayoutGrid,
  LifeBuoy,
  LineChart,
  Library,
  ListChecks,
  MapPinned,
  Megaphone,
  MessageSquare,
  Newspaper,
  PenLine,
  Scale,
  School,
  ScrollText,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  UserCheck,
  UserPlus,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import type { GroupId, ModuleId, PortalOpsId } from './modulesConfig';

/** Tier-1 portal module icons (side rail + mobile module drawer). */
export const MODULE_ICONS: Record<ModuleId, LucideIcon> = {
  comms: MessageSquare,
  complaints: AlertTriangle,
  referrals: UserPlus,
  'human-resources': Users,
  finance: Wallet,
  insights: BarChart3,
  // PRO-0920 — the Commercial (CDP) module. Distinct from the `commercial` GROUP
  // glyph (Briefcase) so the grouped rail header and the module link read differently.
  commercial: Landmark,
  // PRO-3050 — the attendance register.
  attendance: UserCheck,
  // PRO-0710 — trips and visits.
  trips: Bus,
  // PRO-3100 — operational issues.
  'weekly-report': ClipboardList,
  // PRO-3140 — Ofsted readiness.
  ofsted: ShieldCheck,
  // PRO-3210 — Marketing Requests.
  marketing: Megaphone,
  // PRO-3220 Phase 3 — pupil risk assessments.
  'pupil-risk': ShieldAlert,
};

/** Insights lens icons (PRO-0590 — Enterprise / School / Student sub-nav). */
export const INSIGHTS_LENS_ICONS: Record<'enterprise' | 'school' | 'student', LucideIcon> = {
  enterprise: LineChart,
  school: School,
  student: GraduationCap,
};

/** Portal-ops cluster icons (L6 platform tier). */
export const PORTAL_OPS_ICONS: Record<PortalOpsId, LucideIcon> = {
  platform: Settings,
};

/** Scope/function group-section icons (PRO-0600 / ADR-014 A8 grouped rail). */
export const GROUP_ICONS: Record<GroupId, LucideIcon> = {
  'school-ops': School,
  commercial: Briefcase,
};

/** Account-drawer support glyph (PRO-0550 "Report an issue"; no inline lucide). */
export const ISSUE_REPORT_ICON: LucideIcon = LifeBuoy;

/** Comms tier-2 task nav + Home launchpad tiles (single source of truth). */
export const NAV_ITEM_ICONS: Record<string, LucideIcon> = {
  home: LayoutGrid,
  compose: PenLine,
  approvals: CheckCircle2,
  messages: MessageSquare,
  inbox: MessageSquare,
  'comms-settings': Settings,
  'school-templates': FileText,
  operations: Building2,
  // PRO-1260 Epic 3 — school leadership placements board (distinct from commercial-data).
  'commercial-placements': ClipboardList,
  'commercial-won-opportunities': Handshake,
  // PRO-3060 — parent/carer surveys (Comms module).
  surveys: ListChecks,
};

/** Nested sub-module icons (tier-2 sub-module picker) — one distinct glyph each. */
export const SUB_MODULE_ICONS: Record<string, LucideIcon> = {
  // Complaints
  'record-a-complaint': FilePlus2,
  'manage-complaints': ClipboardList,
  // Referrals
  dashboard: LayoutDashboard,
  'register-a-referral': FileInput,
  'qualify-referrals': CheckCircle2,
  'manage-referral-queue': LayoutGrid,
  'my-coverage': UserCheck,
  oversight: LineChart,
  'service-profiles': Building2,
  // Human Resources
  'complete-appraisal': ClipboardList,
  'request-new-hire-atr': UserPlus,
  'people-assistant': Sparkles,
  'interview-compliance': ShieldCheck,
  // PRO-1200 / ADR-062 — the Policies register. `ScrollText` reads as a governed standing
  // document, distinct from the `FileText` used for templates and the HR knowledge tab.
  policies: ScrollText,
  // PRO-1350 Epic 6 — shared People/Finance mailbox workbench (same id in both sections).
  'service-operations': Inbox,
  // PRO-2010 — HR Cases. `FolderOpen` reads as a case file and is unused elsewhere.
  'hr-cases': FolderOpen,
  // Finance
  'claim-expense': Wallet,
  'buy-something': FilePlus2,
};

/**
 * Tier-2 task icons for nested sub-module task tabs (`SubModuleTaskNav`), keyed by
 * the task id in `modulesConfig`. Falls back to the parent nav-item map, then a
 * neutral glyph, so an unmapped task never crashes the nav.
 */
export const TASK_ICONS: Record<string, LucideIcon> = {
  home: LayoutGrid,
  'new-complaint': FilePlus2,
  'my-complaints': ListChecks,
  'in-progress': Clock,
  resolved: CheckCircle2,
  'my-appraisal': ClipboardList,
  'my-team': Users,
  'my-requests': ListChecks,
  // PRO-3190 — the estate-wide ATR register. `Library` reads as the whole set of
  // requests, distinct from the personal `ListChecks` on My requests.
  'all-requests': Library,
  'pending-approval': Clock,
  schedule: CalendarClock,
  'panel-feedback': MessageSquare,
  'my-claims': ListChecks,
  'new-referral': FilePlus2,
  'my-registrations': ListChecks,
  'awaiting-review': Clock,
  escalated: AlertTriangle,
  returned: CheckCircle2,
  'at-risk': AlertTriangle,
  // Human Resources — People Assistant (PRO-1100) + Interview Compliance (PRO-1120)
  knowledge: FileText,
  checklist: ListChecks,
  oversight: LineChart,
  // PRO-1290 — Employee Relations meeting compliance. `Scale` reads as formal procedure
  // and is unused elsewhere, so the tab is distinguishable from Interviews at a glance.
  'employee-relations': Scale,
  // PRO-1500 — the Recruitment "Approved to hire" worklist. `Megaphone` reads as
  // advertising a post, which is what the tab's work actually is, and is unused
  // elsewhere so it stays distinguishable from the ATR approval tabs.
  'approved-to-hire': Megaphone,
  // PRO-2010 — a colleague's own cases (Ask TAG) and the HR Cases handler queue.
  'my-hr-cases': FolderOpen,
  queue: Inbox,
  // PRO-3050 — attendance tasks.
  'attendance-take': ClipboardList,
  'attendance-today': Clock,
  'attendance-reports': LineChart,
  'attendance-settings': Settings,
  // PRO-0710 — trips tasks.
  'trips-register': MapPinned,
  'trips-review': ClipboardCheck,
  'trips-settings': Settings,
  'trips-activities': Library,
  'trips-reviews-due': CalendarClock,
  // PRO-3100 — Weekly Report tasks.
  'ops-position': LayoutDashboard,
  'ops-issues': ClipboardList,
  'ops-portfolio': Building2,
  'ops-group': Landmark,
  'ops-my-actions': ListChecks,
  'my-work': ListChecks,
  'ops-taxonomy': Settings,
  'wr-this-week': PenLine,
  'wr-submissions': ClipboardCheck,
  'wr-history': ScrollText,
  'wr-setup': CalendarClock,
  // PRO-3140 — Ofsted tasks.
  'ofsted-readiness': ClipboardCheck,
  'ofsted-group': Landmark,
  'ofsted-frameworks': ListChecks,
  'ofsted-provisions': Building2,
  // PRO-3150 — the inspection workspace.
  'ofsted-inspection': ScrollText,
  'ofsted-inspection-group': LayoutGrid,
  // PRO-3210 — Marketing tasks.
  'marketing-ask': FilePlus2,
  'marketing-mine': Inbox,
  'marketing-team': Users,
  'marketing-category': LayoutGrid,
  'marketing-all': ListChecks,
  'marketing-calendar': CalendarDays,
  'marketing-log': PenLine,
  'marketing-default-owners': Settings,
  'marketing-settings': Settings,
  'marketing-provision': School,
  'marketing-exec': Newspaper,
  // PRO-3220 Phase 3 — pupil risk tasks.
  'pupil-risk-home': Users,
  'pupil-risk-register': ListChecks,
  'pupil-risk-settings': Settings,
};

export function iconForModule(id: ModuleId | string): LucideIcon {
  return MODULE_ICONS[id as ModuleId] ?? LayoutGrid;
}

export function iconForPortalOps(id: PortalOpsId): LucideIcon {
  return PORTAL_OPS_ICONS[id];
}

export function iconForGroup(id: GroupId | string): LucideIcon {
  return GROUP_ICONS[id as GroupId] ?? LayoutGrid;
}

export function iconForNavItem(id: string): LucideIcon {
  return NAV_ITEM_ICONS[id] ?? LayoutGrid;
}

export function iconForSubModule(id: string): LucideIcon {
  return SUB_MODULE_ICONS[id] ?? LayoutGrid;
}

export function iconForTask(id: string): LucideIcon {
  return TASK_ICONS[id] ?? NAV_ITEM_ICONS[id] ?? LayoutGrid;
}
